import { aesGcmDecrypt, aesGcmEncrypt, generateSalt } from './aes';
import { AES_GCM_PARAMS, PBKDF2_ITERATIONS } from './constants';
import { arrayBufferToBase64, base64ToArrayBuffer, getWebCrypto } from './encoding';
import { randomBytes } from './random';

const LINK_KEY_BYTES = 32;

export function generateLinkKey(): string {
  const bytes = randomBytes(LINK_KEY_BYTES);
  const base64 = arrayBufferToBase64(bytes.buffer as ArrayBuffer);
  return base64.replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
}

async function deriveKeyFromLinkKey(linkKey: string, salt: string): Promise<CryptoKey> {
  const cryptoApi = getWebCrypto();
  const secretMaterial = await cryptoApi.subtle.importKey(
    'raw',
    new TextEncoder().encode(linkKey),
    'PBKDF2',
    false,
    ['deriveKey'],
  );

  return cryptoApi.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: base64ToArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    secretMaterial,
    AES_GCM_PARAMS,
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function wrapDekWithLinkKey(dek: CryptoKey, linkKey: string): Promise<string> {
  const cryptoApi = getWebCrypto();
  const salt = generateSalt();
  const derivedKey = await deriveKeyFromLinkKey(linkKey, salt);
  const rawDek = await cryptoApi.subtle.exportKey('raw', dek);
  const { ciphertext, iv } = await aesGcmEncrypt(rawDek, derivedKey);
  return `${salt}.${iv}.${ciphertext}`;
}

export async function unwrapDekWithLinkKey(wrapped: string, linkKey: string): Promise<CryptoKey> {
  const cryptoApi = getWebCrypto();
  const [salt, iv, ciphertext] = wrapped.split('.');
  if (!salt || !iv || !ciphertext) {
    throw new Error('Invalid link-v1 wrapped DEK format');
  }

  const derivedKey = await deriveKeyFromLinkKey(linkKey, salt);
  const rawDek = await aesGcmDecrypt(ciphertext, derivedKey, iv);

  return cryptoApi.subtle.importKey('raw', rawDek, AES_GCM_PARAMS, true, ['encrypt', 'decrypt']);
}
